<template>
  <v-card
    class="ma-0 px-1 app-form-page"
    :class="{
      'app-form-page--fixed': !isEmbedded && !isMobile,
      'app-form-page--mobile': isMobile && !isEmbedded,
      'app-form-page--embedded': isEmbedded,
    }"
    width="100%"
    flat
  >
    <!-- Mobile header (create always available — same as desktop) -->
    <div v-if="isMobile && !isEmbedded" class="amf-header">
      <div class="amf-header__bar">
        <div class="amf-header__bar-actions">
          <button
            type="button"
            class="amf-header__btn"
            :aria-label="$t(typeForm + 'List')"
            @click="goTo(typeForm + 'List')"
          >
            <v-icon size="20" color="#37474F">mdi-format-list-numbered</v-icon>
          </button>
        </div>
        <h1 class="amf-header__title">{{ $t($route.name) }}</h1>
        <button
          type="button"
          class="amf-header__btn amf-header__btn--back"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>
    </div>

    <!-- Desktop / embedded toolbar -->
    <v-app-bar
      v-else
      flat
      class="my-0 app-form-toolbar"
      :class="
        isMobile
          ? 'mb-2 py-1 px-2 app-form-toolbar--mobile'
          : 'mb-0 py-0 px-2'
      "
      dense
      :height="!isEmbedded && !isMobile ? 40 : undefined"
    >
      <v-toolbar-title class="app-form-toolbar__title me-3">
        {{ $t($route.name) }}
      </v-toolbar-title>
      <v-spacer />
      <div class="app-form-toolbar__actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(typeForm)"
            >
              <v-icon color="teal"> mdi-plus-box-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(typeForm) }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(typeForm + 'List')"
            >
              <v-icon color="primary"> mdi-format-list-numbered </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(typeForm + "List") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="$router.go(-1)"
            >
              <v-icon color="primary"> mdi-arrow-left </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("back") }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>
    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />
    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="app-form-page__form"
      lazy-validation
    >
      <div v-if="isMobile && !isEmbedded" class="amf-mobile-nav" dir="rtl">
        <div class="amf-mobile-nav__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="amf-mobile-nav__tab"
            :class="{ 'amf-mobile-nav__tab--active': formTab === 0 }"
            :aria-selected="formTab === 0"
            @click="formTab = 0"
          >
            {{ $t("public-information") }}
          </button>
          <button
            type="button"
            role="tab"
            class="amf-mobile-nav__tab"
            :class="{ 'amf-mobile-nav__tab--active': formTab === 1 }"
            :aria-selected="formTab === 1"
            :disabled="!documentPermmission"
            @click="documentPermmission && (formTab = 1)"
          >
            {{ $t("documents") }}
          </button>
        </div>
        <div
          v-if="typeForm === 'cost' || typeForm === 'income'"
          class="amf-header__type"
        >
          <button
            type="button"
            class="amf-type-chip"
            :class="{ 'amf-type-chip--active': typeForm === 'cost' }"
            :disabled="!!id"
            @click="!id && goTo('cost')"
          >
            {{ $t("cost") }}
          </button>
          <button
            type="button"
            class="amf-type-chip"
            :class="{ 'amf-type-chip--active': typeForm === 'income' }"
            :disabled="!!id"
            @click="!id && goTo('income')"
          >
            {{ $t("income") }}
          </button>
        </div>
      </div>

      <v-tabs
        v-model="formTab"
        class="app-form-page__tabs"
        :class="{ 'app-form-page__tabs--mobile-bar-hidden': isMobile && !isEmbedded }"
        :show-arrows="isMobile"
      >
        <v-tab>
          {{ $t("public-information") }}
        </v-tab>
        <v-tab :disabled="!documentPermmission">
          {{ $t("documents") }}
        </v-tab>
        <v-tab-item>
          <div v-if="isMobile && !isEmbedded" class="amf-summary" dir="rtl">
            <div v-if="taxAmount || discount" class="amf-summary__status">
              <div v-if="taxAmount" class="amf-summary__chip amf-summary__chip--green">
                {{ $t("tax") }} : {{ $numberFormat(taxAmount) }}
              </div>
              <div v-if="discount" class="amf-summary__chip amf-summary__chip--pink">
                {{ $t("discount") }} : {{ $numberFormat(discount) }}
              </div>
              <div class="amf-summary__chip amf-summary__chip--accent">
                {{ $t("sum") }} : {{ $numberFormat(sum) }}
              </div>
            </div>
            <button
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refDetail')"
            >
              <span class="amf-summary__label">{{ $t(typeForm) }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !detail }"
              >{{ mobileDetailText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refAccount')"
            >
              <span class="amf-summary__label">{{ $t('account') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !account }"
              >{{ mobileAccountText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refCurrency')"
            >
              <span class="amf-summary__label">{{ $t('currency') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !currency }"
              >{{ mobileCurrencyText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="openAmountNumpad">
              <span class="amf-summary__label">{{ $t('amount') }}</span>
              <span class="amf-summary__value">{{ mobileAmountText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="openRateNumpad">
              <span class="amf-summary__label">{{ $t('currency-rate') }}</span>
              <span class="amf-summary__value">{{ mobileRateText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="amf-summary__row" @click="dateSheet = true">
              <span class="amf-summary__label">{{ $t('date') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !date }"
              >{{ date || '—' }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row"
              @click="openMobileAutocomplete('refBank')"
            >
              <span class="amf-summary__label">{{ $t('bank') + '/' + $t('cash') }}</span>
              <span
                class="amf-summary__value"
                :class="{ 'amf-summary__value--empty': !bank }"
              >{{ mobileBankText }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row amf-summary__row--tall"
              @click="optionsSheet = true"
            >
              <span class="amf-summary__label">{{ $t('tax') }} / {{ $t('discount') }}</span>
              <span class="amf-summary__value amf-summary__value--muted">{{ mobileOptionsHint }}</span>
              <v-icon size="22" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="amf-summary__row amf-summary__row--tall"
              @click="otherSheet = true"
            >
              <span class="amf-summary__label">{{ $t('otherInputs') }}</span>
              <span class="amf-summary__value amf-summary__value--muted">{{ $t('other-inputs-hint') }}</span>
              <v-icon size="22" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <button
              v-if="documentPermmission"
              type="button"
              class="amf-summary__row"
              @click="formTab = 1"
            >
              <span class="amf-summary__label">{{ $t('documents') }}</span>
              <span class="amf-summary__value">{{
                hasAttachmentFiles
                  ? attachments.length + ' فایل'
                  : $t('no-data-found')
              }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
            <div class="amf-sr-only" aria-hidden="true">
              <v-text-field v-model="date" />
              <v-text-field v-model="price" :rules="[(v) => !!v || $t('is-required')]" />
              <v-text-field
                v-model="currency_amount"
                :rules="[(v) => !!v || $t('is-required')]"
              />
              <autocomplete
                ref="refDetail"
                v-model="detail"
                :rules="[(v) => !!v || $t('is-required')]"
                :title="$t(typeForm)"
                type="alldetail"
                :value="detail"
              />
              <autocomplete
                ref="refAccount"
                v-model="account"
                title="account"
                type="person"
                :new-btn="true"
                :value="account"
              />
              <autocomplete
                ref="refCurrency"
                v-model="currency"
                :rules="[(v) => !!v || $t('is-required')]"
                :title="$t('currency')"
                type="currency"
                :init="defaultCurrency"
                :value="currency"
                @change="changeCurrency"
              />
              <autocomplete
                ref="refBank"
                v-model="bank"
                :rules="bankRule"
                :title="$t('bank') + '/' + $t('cash')"
                :currency-prop="currency ? currency.id : null"
                type="bankCash"
              />
            </div>
          </div>

          <div v-if="!isMobile || isEmbedded" class="app-form-body">
            <v-container fluid class="app-form-container app-form-container--cards pa-3">
              <v-row class="app-form-card-grid ma-0" dense>
                <!-- Main cost / income card -->
                <v-col cols="12" md="7" class="pa-2">
                  <div class="app-form-card">
                    <div class="app-form-card__header">
                      <div class="app-form-card__icon" aria-hidden="true">
                        <v-icon color="primary" size="22">
                          {{
                            typeForm === "income"
                              ? "mdi-cash-plus"
                              : "mdi-cash-minus"
                          }}
                        </v-icon>
                      </div>
                      <div class="app-form-card__heading">
                        <div class="app-form-card__title">
                          {{ $t("public-information") }}
                        </div>
                        <div class="app-form-card__subtitle">
                          {{
                            typeForm === "income"
                              ? $t("income-main-info-hint")
                              : $t("cost-main-info-hint")
                          }}
                        </div>
                      </div>
                    </div>
                    <div class="app-form-card__body">
                      <v-row class="app-form-fields ma-0">
                        <v-col cols="12" sm="4">
                          <autocomplete
                            v-model="detail"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :title="$t(typeForm)"
                            type="alldetail"
                            :value="detail"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <autocomplete
                            v-model="account"
                            title="account"
                            type="person"
                            :new-btn="true"
                            :value="account"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <autocomplete
                            v-model="currency"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :title="$t('currency')"
                            type="currency"
                            :init="defaultCurrency"
                            :value="currency"
                            @change="changeCurrency"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <money
                            v-model="price"
                            :label="$t('amount')"
                            :dense="true"
                            :outlined="true"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :options="options"
                            @input="checkPrice"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <money
                            v-model="currency_amount"
                            :label="$t('currency-rate')"
                            :dense="true"
                            :outlined="true"
                            :rules="[(v) => !!v || $t('is-required')]"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :options="options"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <template>
                            <v-text-field
                              id="from"
                              v-model="date"
                              :label="$t('date')"
                              clear-icon="flaticon2-close-cross"
                              outlined
                              dense
                              clearable
                              hide-details="auto"
                            />
                            <date-picker
                              v-model="date"
                              element="from"
                              :locale="$store.state.date_locale"
                              :format="$store.state.date_format"
                              auto-submit
                              :min="$store.state.startDate"
                              :max="$store.state.endDate"
                              :color="
                                this.$vuetify.theme.themes[
                                  this.$vuetify.theme.isDark ? 'dark' : 'light'
                                ].primary
                              "
                              @close="setDate"
                            />
                          </template>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <autocomplete
                            ref="refBank"
                            v-model="bank"
                            :rules="bankRule"
                            :title="$t('bank') + '/' + $t('cash')"
                            :currency-prop="currency ? currency.id : null"
                            type="bankCash"
                          />
                        </v-col>
                        <v-col
                          v-if="typeForm !== 'commission' && typeForm !== 'penalty'"
                          cols="12"
                          sm="4"
                          class="d-flex align-center"
                        >
                          <v-checkbox
                            v-model="taxFeilds"
                            class="mt-0"
                            hide-details
                            dense
                            :label="$t('tax')"
                          />
                        </v-col>
                        <v-col
                          v-if="
                            account &&
                            typeForm !== 'commission' &&
                            typeForm !== 'penalty'
                          "
                          cols="12"
                          sm="4"
                          class="d-flex align-center"
                        >
                          <v-checkbox
                            v-model="refundStatus"
                            class="mt-0"
                            hide-details
                            dense
                            :label="$t('refund')"
                          />
                        </v-col>
                        <v-col
                          v-if="typeForm !== 'commission' && typeForm !== 'penalty'"
                          cols="12"
                          sm="4"
                          class="d-flex align-center"
                        >
                          <v-checkbox
                            v-model="discountFeilds"
                            class="mt-0"
                            hide-details
                            dense
                            :disabled="this.taxStatus === 'notAdd' ? true : false"
                            :label="$t('discount')"
                          />
                        </v-col>
                        <v-col v-if="refundStatus && account" cols="12" sm="4">
                          <money
                            v-model="refund"
                            :label="$t('refundAmount')"
                            :dense="true"
                            :outlined="true"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :options="options"
                          />
                        </v-col>
                        <template v-if="taxFeilds">
                          <v-col cols="12" sm="4">
                            <vuetify-money
                              v-model="tax"
                              :label="$t('tax')"
                              :dense="true"
                              :outlined="true"
                              :solo="false"
                              :clearable="true"
                              value-when-is-empty=""
                              :options="optionsTax"
                            />
                          </v-col>
                          <v-col cols="12" sm="4">
                            <vuetify-money
                              v-model="taxAmount"
                              :label="$t('amount') + ' ' + $t('tax')"
                              :dense="true"
                              :disabled="true"
                              :outlined="true"
                              :solo="false"
                              :clearable="true"
                              value-when-is-empty=""
                              :options="options"
                            />
                          </v-col>
                          <v-col cols="12" sm="4">
                            <v-select
                              v-model="taxStatus"
                              :label="$t('taxStatus')"
                              :items="taxStatusItems"
                              height="30"
                              dense
                              outlined
                              hide-details="auto"
                              item-value="value"
                              item-text="text"
                            />
                          </v-col>
                        </template>
                        <v-col v-if="discountFeilds" cols="12" sm="4">
                          <vuetify-money
                            v-model="discount"
                            :label="$t('discount')"
                            :dense="true"
                            :outlined="true"
                            :solo="false"
                            :clearable="true"
                            value-when-is-empty=""
                            :options="options"
                            :value-options="{ max: price }"
                            @SetValueMax="SetValueMaxDiscount()"
                          />
                        </v-col>
                        <v-col
                          v-if="payItems && payItems.length > 0"
                          cols="12"
                        >
                          <v-chip
                            v-for="(item, key) in payItems"
                            :key="key"
                            class="ma-1"
                            :close="item.chip"
                            :color="item.chip ? item.color : 'grey'"
                            outlined
                            @click:close="item.chip = false"
                          >
                            <v-icon
                              v-if="!item.chip"
                              color="green"
                              @click="item.chip = true"
                            >
                              mdi-refresh
                            </v-icon>
                            {{ item.type == "pay_naghd" ? $t("cashPay") : "" }}
                            - {{ $t("price") }} :
                            {{ $numberFormat(parseFloat(item.amount)) }}
                          </v-chip>
                        </v-col>
                      </v-row>
                      <div class="app-form-card__status">
                        <v-row class="app-form-fields app-form-card__status-row ma-0">
                          <v-col v-if="taxAmount" cols="12" sm="6">
                            <v-chip
                              class="app-form-status-chip mx-0"
                              label
                              dark
                              outlined
                              color="orange"
                            >
                              {{ $t("Net-fee") }} : {{ $numberFormat(NetPrice) }}
                            </v-chip>
                          </v-col>
                          <v-col v-if="taxAmount" cols="12" sm="6">
                            <v-chip
                              class="app-form-status-chip mx-0"
                              label
                              dark
                              outlined
                              color="green"
                            >
                              {{ $t("tax") }} : {{ $numberFormat(taxAmount) }}
                            </v-chip>
                          </v-col>
                          <v-col v-if="discount" cols="12" sm="6">
                            <v-chip
                              class="app-form-status-chip mx-0"
                              label
                              dark
                              outlined
                              color="pink"
                            >
                              {{ $t("discount") }} : {{ $numberFormat(discount) }}
                            </v-chip>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-chip
                              class="app-form-status-chip mx-0"
                              label
                              dark
                              outlined
                              color="blue"
                            >
                              {{ $t("sum") }} : {{ $numberFormat(sum) }}
                            </v-chip>
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Other inputs card -->
                <v-col cols="12" md="5" class="pa-2">
                  <div class="app-form-card">
                    <div class="app-form-card__header">
                      <div class="app-form-card__icon" aria-hidden="true">
                        <v-icon color="primary" size="22">mdi-tune-variant</v-icon>
                      </div>
                      <div class="app-form-card__heading">
                        <div class="app-form-card__title">
                          {{ $t("otherInputs") }}
                        </div>
                        <div class="app-form-card__subtitle">
                          {{ $t("other-inputs-hint") }}
                        </div>
                      </div>
                    </div>
                    <div class="app-form-card__body app-form-card__body--stack">
                      <v-row class="app-form-fields ma-0">
                        <v-col cols="12" sm="6">
                          <autocomplete
                            v-model="branch"
                            :title="$t('branch')"
                            type="branch"
                            :set-init="true"
                            :value="branch"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <autocomplete
                            v-model="costGiven"
                            :title="$t('given') + ' ' + $t(typeForm)"
                            type="given"
                          />
                        </v-col>
                        <v-col cols="12">
                          <tag v-model="tag" :solo="false" :input="tag" :height="25" />
                        </v-col>
                        <v-col cols="12" class="app-form-fields__grow">
                          <description
                            ref="refDescription"
                            v-model="description"
                            class="payment-description"
                            :outlined="true"
                            :rows="4"
                            :value="description"
                            type="sell"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="app-form-body">
            <v-container class="app-form-container app-form-documents pt-3 pb-2">
              <form-attachment-doc
                ref="refFilePicker"
                v-model="attachments"
                type-prop="costIncome"
                :val="id && attachments ? attachments : null"
              />
            </v-container>
          </div>
        </v-tab-item>
      </v-tabs>

      <template v-if="isMobile && !isEmbedded">
        <v-bottom-sheet v-model="optionsSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('tax') }} / {{ $t('discount') }}</div>
              <button type="button" class="amf-sheet__back" @click="optionsSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-sheet__body--stack">
              <template v-if="typeForm !== 'commission' && typeForm !== 'penalty'">
                <v-checkbox
                  v-model="taxFeilds"
                  class="mt-0"
                  hide-details
                  dense
                  :label="$t('tax')"
                />
                <v-checkbox
                  v-if="account"
                  v-model="refundStatus"
                  class="mt-0"
                  hide-details
                  dense
                  :label="$t('refund')"
                />
                <v-checkbox
                  v-model="discountFeilds"
                  class="mt-0"
                  hide-details
                  dense
                  :disabled="taxStatus === 'notAdd'"
                  :label="$t('discount')"
                />
                <money
                  v-if="refundStatus && account"
                  v-model="refund"
                  :label="$t('refundAmount')"
                  :dense="true"
                  :outlined="true"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                />
                <template v-if="taxFeilds">
                  <vuetify-money
                    v-model="tax"
                    :label="$t('tax')"
                    :dense="true"
                    :outlined="true"
                    :solo="false"
                    :clearable="true"
                    value-when-is-empty=""
                    :options="optionsTax"
                  />
                  <vuetify-money
                    v-model="taxAmount"
                    :label="$t('amount') + ' ' + $t('tax')"
                    :dense="true"
                    :disabled="true"
                    :outlined="true"
                    :solo="false"
                    :clearable="true"
                    value-when-is-empty=""
                    :options="options"
                  />
                  <v-select
                    v-model="taxStatus"
                    :label="$t('taxStatus')"
                    :items="taxStatusItems"
                    dense
                    outlined
                    hide-details="auto"
                    item-value="value"
                    item-text="text"
                  />
                </template>
                <vuetify-money
                  v-if="discountFeilds"
                  v-model="discount"
                  :label="$t('discount')"
                  :dense="true"
                  :outlined="true"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                  :value-options="{ max: price }"
                  @SetValueMax="SetValueMaxDiscount()"
                />
              </template>
              <div v-else class="amf-summary__value amf-summary__value--muted">
                —
              </div>
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="optionsSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="otherSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('otherInputs') }}</div>
              <button type="button" class="amf-sheet__back" @click="otherSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-sheet__body--stack">
              <autocomplete
                v-model="branch"
                :title="$t('branch')"
                type="branch"
                :set-init="true"
                :value="branch"
              />
              <autocomplete
                v-model="costGiven"
                :title="$t('given') + ' ' + $t(typeForm)"
                type="given"
              />
              <tag v-model="tag" :solo="false" :input="tag" :height="25" />
              <description
                ref="refDescription"
                v-model="description"
                class="payment-description"
                :outlined="true"
                :rows="4"
                :value="description"
                type="sell"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="otherSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="dateSheet">
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t('date') }}</div>
              <button type="button" class="amf-sheet__back" @click="dateSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-mobile-date" dir="rtl">
              <div class="amf-mobile-date__selected">
                <span class="amf-mobile-date__label">{{ $t('date') }}</span>
                <strong class="amf-mobile-date__value">{{ date || '—' }}</strong>
                <button
                  v-if="date"
                  type="button"
                  class="amf-mobile-date__clear"
                  @click="date = null"
                >
                  {{ $t("clear") }}
                </button>
              </div>
              <date-picker
                v-model="date"
                inline
                simple
                auto-submit
                type="date"
                :locale="$store.state.date_locale"
                :format="$store.state.date_format"
                :min="$store.state.startDate"
                :max="$store.state.endDate"
                class="amf-mobile-date__picker"
                color="#7B8BA3"
                @close="setDate"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="dateSheet = false">
                {{ $t("Save") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
          <v-card class="amf-numpad" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-numpad__header">
              <span class="amf-numpad__header-spacer" />
              <div class="amf-numpad__title">{{ numpadLabel }}</div>
              <button type="button" class="amf-sheet__back" @click="numpadOpen = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-numpad__display">
              <div class="amf-numpad__display-label">{{ numpadLabel }}</div>
              <div class="amf-numpad__display-value">{{ numpadDisplay }}</div>
            </div>
            <div class="amf-numpad__pad" dir="ltr">
              <div class="amf-numpad__digits">
                <button
                  v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
                  :key="'d-' + n"
                  type="button"
                  class="amf-numpad__key"
                  @click="numpadDigit(n)"
                >
                  {{ n }}
                </button>
                <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('00')">00</button>
                <button type="button" class="amf-numpad__key" @click="numpadDigit(0)">0</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('000')">000</button>
              </div>
              <div class="amf-numpad__actions">
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(1)">+</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(-1)">-</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadClear">C</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadDot">.</button>
                <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadBackspace">
                  <v-icon size="20" color="#5B7C99">mdi-backspace-outline</v-icon>
                </button>
              </div>
            </div>
            <div class="amf-numpad__footer">
              <button type="button" class="amf-sheet__btn" @click="confirmNumpad">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>
      </template>

      <div class="app-form-page__actions">
        <div class="app-form-page__actions-inner">
          <v-btn
            v-if="!id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="create"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="update"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            color="primary"
            outlined
            depressed
            class="app-form-action-btn"
            @click="goTo(typeForm + 'List')"
          >
            {{ $t(typeForm + "List") }}
          </v-btn>
        </div>
      </div>
    </v-form>
    <snakbar
      v-model="snakbar"
      :text="snakbarText"
      :active="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
    />
  </v-card>
</template>

<script>
import Description from "../../components/general/description/Description";
import General from "../../../../Mixins/general";
import FormAttachmentDoc from "../../components/general/FormAttachmentDoc";
export default {
  name: "Customer",
  components: {
    autocomplete: () => import("../../components/general/patial/autocomplete"),
    Tag: () => import("../../components/general/Tag/tag"),
    Snakbar: () => import("../../components/general/snakbar/snakbar"),
    FormAttachmentDoc,
    Description,
  },
  mixins: [General],
  props: ["type", "refId"],
  data: () => ({
    extra: false,
    valid: true,
    attachments: null,
    id: null,
    ref_type: null,
    ref_id: null,
    refundStatus: false,
    refund: 0,
    costGiven: null,
    currency: null,
    currency_amount: 1,
    tag: null,
    customerId: null,
    defaultCurrency: null,
    taxStatus: null,
    typeForm: "cost",
    taxStatusItems: null,
    clearing: null,
    snackbarShow: null,
    payed: 0,
    payItems: [],
    price: 0,
    NetPrice: 0,
    NetPriceEdit: 0,
    taxAmount: 0,
    tax: 9,
    discount: 0,
    plan: null,
    taxFeilds: null,
    discountFeilds: null,
    account: null,
    options: {
      locale: "en-US",
      prefix: "",
      suffix: "  ",
      precision: 0,
    },
    optionsTax: {
      locale: "en-US",
      prefix: "",
      suffix: "  %  ",
      precision: 0,
    },
    detail: null,
    editPrice: 0,
    bank: null,
    description: null,
    date: null,
    render: true,
    isLoading: false,
    showing: false,
    snakbar: null,
    snakbarText: null,
    snakbarColor: null,
    formTab: 0,
    optionsSheet: false,
    otherSheet: false,
    dateSheet: false,
    numpadOpen: false,
    numpadField: null,
    numpadLabel: "",
    numpadDraft: "",
    numpadIsAmount: true,
  }),
  computed: {
    isEmbedded() {
      return !!this.refId;
    },
    bankRule() {
      const rules = [];
      if (!this.account) {
        const rule = (v) => !!v || this.$t("is-required");
        rules.push(rule);
      }

      return rules;
    },
    sum() {
      if (this.taxStatus === "notAdd") {
        return parseFloat(this.price) - parseFloat(this.discount);
      } else {
        return (
          parseFloat(this.price) - parseFloat(this.discount) + parseFloat(this.taxAmount)
        );
      }
    },
    mobileDetailText() {
      if (!this.detail) return "—";
      return this.detail.name || this.detail.title || "—";
    },
    mobileAccountText() {
      if (!this.account) return "—";
      return this.account.name || this.account.title || "—";
    },
    mobileCurrencyText() {
      if (!this.currency) return "—";
      return this.currency.name || "—";
    },
    mobileBankText() {
      if (!this.bank) return "—";
      return this.bank.name || this.bank.title || "—";
    },
    mobileAmountText() {
      if (this.price === null || this.price === undefined || this.price === "") {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.price, "plain", 0)
        : String(this.price);
    },
    mobileRateText() {
      if (
        this.currency_amount === null ||
        this.currency_amount === undefined ||
        this.currency_amount === ""
      ) {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.currency_amount, "plain", 0)
        : String(this.currency_amount);
    },
    mobileOptionsHint() {
      const parts = [];
      if (this.taxFeilds) parts.push(this.$t("tax"));
      if (this.discountFeilds) parts.push(this.$t("discount"));
      if (this.refundStatus) parts.push(this.$t("refund"));
      return parts.length ? parts.join(" / ") : "—";
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft || "");
      if (!raw) return "0";
      if (!this.numpadIsAmount || !this.$numberFormat) return raw;
      if (raw.endsWith(".")) {
        const head = raw.slice(0, -1);
        return (head ? this.$numberFormat(head, "plain", 0) : "0") + ".";
      }
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return raw;
      return this.$numberFormat(n, "plain", 0);
    },
    hasAttachmentFiles() {
      return Array.isArray(this.attachments) && this.attachments.length > 0;
    },
  },
  watch: {
    defaultCurrency() {
      if (this.defaultCurrency) {
        this.currency = this.defaultCurrency;
        this.currency_amount = this.defaultCurrency.amount;
      }
    },
    tax() {
      this.calculateTax();
    },
    taxFeilds() {
      this.calculateTax();
    },
    discount() {
      this.calculateTax();
    },
    bank() {
      if (this.bank && this.typeForm === "cost") {
        this.remainBank(this.price);
      }
    },
    price() {
      this.calculateTax();
    },
    taxStatus() {
      this.calculateTax();
    },
    currency(val, old) {
      if (val) {
        if (this.render && !this.showing) {
          this.currency_amount = val.amount;
          this.bank = null;
        }
      } else {
        this.currency_amount = null;
      }
      this.showing = false;
    },
    type() {
      this.typeForm = this.type;
    },
    isMobile(val) {
      if (this.isEmbedded) return;
      this.lockPageScroll(!!val);
    },
  },
  beforeMount() {
    this.typeForm = this.$route.name;
    if (this.type) {
      this.typeForm = this.type;
    }
    if (!this.type && this.$route.params.id != null) {
      this.id = this.$route.params.id;
      this.render = false;
      this.clearForm();
      this.show();
    } else {
      this.currency = JSON.parse(localStorage.getItem("token")).init.currency;
      this.render = true;
      this.defaultCurrency = this.getDefault("defaultCurrency");
    }
  },
  mounted() {
    this.taxStatusItems = [
      {
        value: "add",
        text: this.$t("add-tax-to-price"),
      },
      {
        value: "notAdd",
        text: this.$t("calculate-tax-on-price"),
      },
    ];
    this.taxStatus = "notAdd";
    if (this.isMobile && !this.isEmbedded) {
      this.lockPageScroll(true);
    }
  },
  beforeDestroy() {
    this.lockPageScroll(false);
  },
  methods: {
    openMobileAutocomplete(refName) {
      const ref = this.$refs[refName];
      if (!ref || typeof ref.openMobileDialog !== "function") return;
      if (ref.disabled) return;
      ref.openMobileDialog();
    },
    lockPageScroll(lock) {
      const value = lock ? "hidden" : "";
      const overscroll = lock ? "none" : "";
      document.documentElement.style.overflow = value;
      document.documentElement.style.overscrollBehavior = overscroll;
      document.body.style.overflow = value;
      document.body.style.overscrollBehavior = overscroll;
      const main = document.querySelector(".v-main");
      const wrap = document.querySelector(".v-main__wrap");
      if (main) {
        main.style.overflow = value;
        main.style.overscrollBehavior = overscroll;
      }
      if (wrap) {
        wrap.style.overflow = value;
        wrap.style.overscrollBehavior = overscroll;
      }
    },
    openAmountNumpad() {
      this.numpadField = "price";
      this.numpadLabel = this.$t("amount");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.price !== null && this.price !== undefined && this.price !== ""
          ? String(this.price)
          : "";
      this.numpadOpen = true;
    },
    openRateNumpad() {
      this.numpadField = "currency_amount";
      this.numpadLabel = this.$t("currency-rate");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.currency_amount !== null &&
        this.currency_amount !== undefined &&
        this.currency_amount !== ""
          ? String(this.currency_amount)
          : "";
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    confirmNumpad() {
      if (this.numpadField) {
        const raw = String(this.numpadDraft || "").trim();
        const parsed = raw === "" ? 0 : parseFloat(raw);
        this[this.numpadField] = Number.isNaN(parsed) ? 0 : parsed;
        if (this.numpadField === "price") {
          this.checkPrice();
        }
      }
      this.numpadField = null;
      this.numpadOpen = false;
    },
    remainBank(price = 0) {
      if (this.bank && this.bank.remain !== undefined) {
        // if (this.$route.params.id != null) {
        //   this.bankRemain = parseFloat(this.bank.remain.currency_remain) - parseFloat(price) + parseFloat(this.editPrice)
        // } else {
        //   this.bankRemain = parseFloat(this.bank.remain.currency_remain) - parseFloat(price)
        // }
        //
        // this.bankRemainColor = 'grey'
        // if (this.bankRemain > 0) {
        //   this.bankRemainColor = 'green'
        // }
        // if (this.bankRemain < 0) {
        //   this.bankRemainColor = 'pink'
        // }
        // if (this.bank && parseFloat(price) > parseFloat(this.bank.remain.currency_remain) + parseFloat(this.editPrice)) {
        //   this.price = 0
        //   this.bankRemain = parseFloat(this.bank.remain.currency_remain) - parseFloat(price) + parseFloat(this.editPrice)
        //   this.snakbarText = this.$t('alert-pay-amount-bigger-than-bank-remain')
        //   this.snakbarColor = 'pink'
        //   this.snakbar = true
        // }
      }
    },
    checkPrice() {
      if (this.taxFeilds) {
        this.calculateTax();
      }
      if (this.bank && this.typeForm === "cost") {
        // this.remainBank(this.price)
      }
    },
    changeCurrency() {
      this.bankRemain = 0;
      this.currency_amount = this.currency.amount;
      this.options = {
        locale: "en-US",
        prefix: "",
        suffix: this.currency.name,
        precision: this.currency.decimal,
      };
    },
    SetValueMaxPrice() {
      if (this.clearing) {
        this.price = this.bankRemain;
      }
    },
    SetValueMaxDiscount() {
      this.discount = this.price;
    },
    create() {
      // if (parseFloat(this.bankRemain) < 0) {
      //   this.snakbarText = this.$t('alert-pay-amount-bigger-than-bank-remain')
      //   this.snakbarColor = 'pink'
      //   this.snakbar = true
      //   return false
      // }
      if (!this.$refs.form.validate()) return false;
      var app = this;
      app.isLoading = true;
      app.$axios
        .post(this.$endpoint + "CostIncome", this.$data)
        .then((res) => {
          var result = res.data.data;
          app.snakbarText = app.$t("registered");
          app.snakbarColor = "green";
          app.snakbar = true;
          app.$emit("close", app.$route);
          if (app.$refs.refFilePicker !== undefined && app.attachments) {
            app.$refs.refFilePicker.create(result.id);
          }
          if (app.refId) {
            var data = {
              name: app.detail.name,
              amount:
                (parseFloat(result.price) + parseFloat(result.tax)) *
                result.currency_amount,
              id: result.id,
            };
            app.$emit("input", data);
          } else {
            app.goTo(app.typeForm + "List");
          }
          this.clearForm();
        })
        .catch((err) => {
          if (err.response && err.response.status === 409) {
            this.$store.commit("SET_PLAN_DIALOG", true);
          }
          this.serverErrorHandeler(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    show() {
      var app = this;
      app.loading = true;
      app.clearing = true;
      app.showing = true;
      app.$axios
        .get(this.$endpoint + "CostIncome/" + this.$route.params.id + "/edit", {
          params: {
            id: this.$route.params.id,
          },
        })
        .then((res) => {
          res = res.data;
          app.typeForm = res.type;
          app.id = res.id;
          app.price = res.price;
          app.NetPriceEdit = res.price;
          app.attachments = res.attachments;
          app.editPrice = res.price;
          app.account = res.customer;
          app.branch = res.branch;
          app.costGiven = res.given;
          app.currency_amount = res.currency_amount;
          app.currency = res.currency;
          app.date = res.date;
          app.plan = res.plan ? res.plan.plan : null;
          app.detail = res.account;
          app.refund = res.refund;
          app.ref_type = res.ref_type;
          app.ref_id = res.ref_id;
          if (app.refund && app.refund > 0) {
            app.refundStatus = true;
          }
          if (res.tags) {
            app.tag = res.tags;
            app.more = true;
          }
          app.taxAmount = res.tax;
          if (app.taxAmount && app.taxAmount > 0) {
            app.taxStatus = app.taxStatusItems[0];
            app.taxFeilds = true;
            setTimeout(function () {
              app.taxAmount = res.tax;
            }, 1000);
          }
          app.discount = res.discount;
          app.bank = res.bank;
          if (app.discount && app.discount > 0) {
            app.discountFeilds = true;
          }
          app.payed = parseFloat(app.price) - parseFloat(res.remain);
          app.payItems = res.relations;
          app.description = res.description;
          // app.$refs.refDescription.setData(res.description)
          // setTimeout(function () {
          //   app.showing = false
          //   app.render = true
          // }, 500)
          app.render = true;
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    update() {
      var app = this;
      if (!this.$refs.form.validate()) return false;
      app.isLoading = true;
      app.$axios
        .put(this.$endpoint + "CostIncome/" + this.$route.params.id, this.$data)
        .then((res) => {
          res = res.data;
          if (app.$refs.refFilePicker !== undefined) {
            app.$refs.refFilePicker.create(res.data.id);
          }
          app.$emit("close", app.$route);
          app.goTo(app.typeForm + "List");
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.isLoading = false));
    },
    calculateTax() {
      if (!this.showing && this.taxFeilds) {
        if (this.taxStatus === "notAdd") {
          this.discountFeilds = null;
          this.discount = 0;
          var price = (parseFloat(this.price) * 100) / (100 + parseInt(this.tax));
          this.taxAmount = parseInt(this.price) - parseInt(price);
          this.NetPrice =
            this.price - this.taxAmount.toFixed(this.$store.state.currency.decimal);
        } else {
          this.taxAmount = (
            (parseFloat(this.price) -
              parseFloat(this.refund) -
              parseFloat(this.discount)) *
            (this.tax / 100)
          ).toFixed(this.$store.state.currency.decimal);
          this.NetPrice = this.price;
        }
      } else {
        this.taxAmount = 0;
        this.NetPrice = this.price;
      }
    },
    clearForm() {
      this.extra = false;
      this.attachments = null;
      this.id = null;
      this.costGiven = null;
      this.tag = null;
      this.customerId = null;
      this.taxStatus = null;
      this.taxStatusItems = null;
      this.clearing = null;
      this.snackbarShow = null;
      this.payed = 0;
      this.price = 0;
      this.NetPrice = 0;
      this.taxAmount = 0;
      this.tax = 9;
      this.discount = 0;
      this.taxFeilds = null;
      this.discountFeilds = null;
      this.account = null;
      this.detail = null;
      this.editPrice = null;
      this.bank = null;
      this.description = null;
    },
  },
};
</script>

<style scoped></style>
